import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

/**
 * Bronson LicensePlate component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-license-plate.html
 *
 * @param {string} [className] - Additional CSS classes on the component element.
 * @param {string} [country] - The country identifier.
 * @param {string} [countryCode] - The country code abbreviation.
 * @param {boolean} [euStars] - Show EU stars.
 * @param {boolean} [horizontalStripEu] - Show EU-specific horizontal stripe.
 * @param {boolean} [horizontalStripJp] - Show Japanese-specific horizontal stripe.
 * @param {boolean} [jp] - Render a Japanese LicensePlate.
 * @param {string} [jpPrefectureCode] - Japanese prefecture code.
 * @param {string} [jpRegistrationPrefix] - Japanese registration prefix.
 * @param {string} [jpVehicleCode] - Japanese vehicle code.
 * @param {boolean} [nl] - Render a Netherlands LicensePlate.
 * @param {string} [registrationNumber] - The registration number.
 * @param {('xsmall'|'small'|'medium'|'large')} [size] - The size of the LicensePlate.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The LicensePlate component.
 * @constructor
 */
export function LicensePlate({
  className,
  country,
  countryCode,
  euStars,
  horizontalStripEu,
  horizontalStripJp,
  jp,
  jpPrefectureCode,
  jpRegistrationPrefix,
  jpVehicleCode,
  nl,
  registrationNumber,
  size,
  testId,
  ...otherProps
}) {
  const articleClassNameList = classNames(
    'c-license-plate',
    {
      [`c-license-plate--${size}`]: size, // Convenience prop from Bronson variants.
      'c-license-plate--nl': nl, // Convenience prop from Bronson variants.
      'c-license-plate--jp': jp, // Convenience prop from Bronson variants.
    },
    className
  ).trim()

  function renderIfLicensePlateEuStars() {
    if (euStars) {
      return <div className="c-license-plate__eu-stars" />
    }
    return null
  }

  function renderIfLicensePlateHorizontalStripEu() {
    if (horizontalStripEu) {
      return (
        <div className="c-license-plate__horizontal-strip c-license-plate__horizontal-strip--eu">
          {renderIfLicensePlateEuStars()}
          <abbr className="c-license-plate__country-code" title={country}>
            {countryCode}
          </abbr>
        </div>
      )
    }
    return null
  }

  function renderIfLicensePlateHorizontalStripJp() {
    if (horizontalStripJp) {
      return (
        <div className="c-license-plate__horizontal-strip c-license-plate__horizontal-strip--jp">
          {jpRegistrationPrefix}
        </div>
      )
    }
    return null
  }

  return (
    <article {...otherProps} data-testid={testId} className={articleClassNameList} aria-label="License Plate">
      {renderIfLicensePlateHorizontalStripEu()}
      {renderIfLicensePlateHorizontalStripJp()}
      <div className="c-license-plate__registration-number" aria-label="Registration Number">
        {jpRegistrationPrefix ? (
          <>
            <span className="c-license-plate__registration-top-bar">{`${jpPrefectureCode} ${jpVehicleCode}`}</span>
            <span className="c-license-plate__registration-bottom-bar">{registrationNumber}</span>
          </>
        ) : (
          <>
            <span className="c-license-plate__registration-bottom-bar">{registrationNumber}</span>
          </>
        )}
      </div>
    </article>
  )
}

LicensePlate.propTypes = {
  className: PropTypes.string, // Additional CSS classes on the component element. Bronson template: 'license-plate-modifier'.
  country: PropTypes.string, // Bronson template: 'license-plate-country'.
  countryCode: PropTypes.string, // Bronson template: 'license-plate-country-code'.
  euStars: PropTypes.bool, // Bronson template: 'license-plate-has-eu-stars'.
  horizontalStripEu: PropTypes.bool, // Bronson template: 'license-plate-has-horizontal-strip-eu'.
  horizontalStripJp: PropTypes.bool, // Bronson template: 'license-plate-has-horizontal-strip-jp'.
  jpPrefectureCode: PropTypes.string, // Bronson template: 'license-plate-jp-prefecture-code'.
  jpRegistrationPrefix: PropTypes.string, // Bronson template: 'license-plate-jp-registration-prefix'.
  jpVehicleCode: PropTypes.string, // Bronson template: 'license-plate-jp-vehicle-code'.
  registrationNumber: PropTypes.string, // Bronson template: 'license-plate-registration-number'.
  testId: PropTypes.string, // Added for the `[data-testid]` attribute.
  /* Convenience props. */
  jp: PropTypes.bool, // Convenience prop for `.c-license-plate--jp`. Bronson template: 'license-plate-modifier'.
  nl: PropTypes.bool, // Convenience prop for `.c-license-plate--nl`. Bronson template: 'license-plate-modifier'.
  size: PropTypes.oneOf(['xsmall', 'small', 'medium', 'large']), // Convenience prop for `.c-license-plate--{xsmall, small, medium, large}`. Bronson template: 'license-plate-modifier'.
}
